import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClipboardList, Network, MonitorPlay, ArrowRight } from "lucide-react";
import "./OpenLiftDeployment.css";

/*
 * OPENlift — Déploiement (section 06)
 * Les textes sont intégrés en FR/EN sans modification des fichiers i18n globaux.
 * Le nom des composants existants et leur fonctionnement restent inchangés.
 */
const CONTENT = {
  fr: {
    eyebrow: "Un accompagnement adapté au terrain",
    titleStart: "Une mise en place accompagnée,",
    titleAccent: "adaptée à vos équipements.",
    introduction:
      "Nous partons de votre parc réel, pas d’une promesse d’installation identique partout.",
    steps: [
      {
        title: "Étudier votre parc",
        description:
          "Identifier les familles de contrôleurs, interfaces et besoins de votre équipe.",
        footer: "Qualification"
      },
      {
        title: "Définir l’intégration",
        description:
          "Choisir la passerelle et la configuration appropriées selon vos équipements.",
        footer: "Configuration"
      },
      {
        title: "Découvrir les données",
        description:
          "Voir la plateforme en démonstration puis, si pertinent, en pilote encadré.",
        footer: "Démonstration / pilote"
      }
    ],
    note:
      "Chaque intégration dépend des modèles, interfaces et configurations du parc ; les modalités sont précisées après étude.",
    technicalLabel: "Pour les équipes techniques",
    technicalText:
      "Selon les contrôleurs, l’étude peut notamment porter sur les interfaces RS232, RS422 ou CAN. Le choix des passerelles, des transports réseau et des accès est confirmé pour chaque configuration.",
    cta: "Demander une démonstration"
  },
  en: {
    eyebrow: "Deployment tailored to the field",
    titleStart: "An assisted deployment,",
    titleAccent: "adapted to your equipment.",
    introduction:
      "We start with your actual fleet, not the promise of an identical installation everywhere.",
    steps: [
      {
        title: "Review your fleet",
        description:
          "Identify controller families, interfaces and your team’s requirements.",
        footer: "Assessment"
      },
      {
        title: "Define the integration",
        description:
          "Select the appropriate gateway and configuration for your equipment.",
        footer: "Configuration"
      },
      {
        title: "Explore the data",
        description:
          "See the platform in a demonstration and, where appropriate, a supervised pilot.",
        footer: "Demonstration / pilot"
      }
    ],
    note:
      "Integration depends on each fleet’s models, interfaces and configurations; arrangements are defined after assessment.",
    technicalLabel: "For technical teams",
    technicalText:
      "Depending on the controller, the review may cover RS232, RS422 or CAN interfaces. Gateways, network transport and access are confirmed for each configuration.",
    cta: "Request a demonstration"
  }
};

function StepIcon({ index }) {
  // Use a refined and consistent visual weight across the three step icons.
  const iconProps = {
    width: 34,
    height: 34,
    strokeWidth: 1.65,
    "aria-hidden": true,
    focusable: "false"
  };

  if (index === 0) return <ClipboardList {...iconProps} />;
  if (index === 1) return <Network {...iconProps} />;
  return <MonitorPlay {...iconProps} />;
}

function Connector() {
  return (
    <span className="ol-deploy__connector" aria-hidden="true">
      <ArrowRight width={38} height={24} strokeWidth={1.6} focusable="false" />
    </span>
  );
}

export default function OpenLiftDeployment() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = CONTENT[lang] || CONTENT.fr;
  const sectionRef = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.18 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`ol-deploy${seen ? " ol-deploy--seen" : ""}`}
      id="ol-deployment"
      aria-labelledby="ol-deploy-title"
    >
      <div className="ol-deploy__container">
        <header className="ol-deploy__header">
          <p className="ol-deploy__eyebrow">{content.eyebrow}</p>
          <h2 className="ol-deploy__title" id="ol-deploy-title">
            <span>{content.titleStart}</span>
            <span className="ol-deploy__titleAccent">{content.titleAccent}</span>
          </h2>
          <p className="ol-deploy__intro">{content.introduction}</p>
        </header>

        <ol className="ol-deploy__steps">
          {content.steps.map((step, index) => (
            <li className="ol-deploy__step" key={index}>
              <span className="ol-deploy__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="ol-deploy__icon"><StepIcon index={index} /></div>
              <h3 className="ol-deploy__stepTitle">{step.title}</h3>
              <p className="ol-deploy__description">{step.description}</p>
              <p className="ol-deploy__stepFooter">{step.footer}</p>
              {index < content.steps.length - 1 && <Connector />}
            </li>
          ))}
        </ol>

        <div className="ol-deploy__bottom">
          <div className="ol-deploy__support">
            <p className="ol-deploy__note">{content.note}</p>
            <details className="ol-deploy__technical">
              <summary>{content.technicalLabel}</summary>
              <p>{content.technicalText}</p>
            </details>
          </div>
          <Link to="/demo" className="ol-deploy__cta">
            <span>{content.cta}</span>
            <ArrowRight width={20} height={20} strokeWidth={1.7} aria-hidden="true" focusable="false" />
          </Link>
        </div>
      </div>
    </section>
  );
}
